import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { PlayerGroups, Roster } from "..";
import { TabContent, TabItem } from "../../components";
import { useUIContext } from "../../contexts/UIContext";
import { usePlayerLoading } from "../../hooks/usePlayerLoading";
import { TabRouteParams } from "../../types/sharedTypes";

export const Players: React.FC = (): JSX.Element => {
  // State and AppContext
  const { setDisplayBack } = useUIContext();

  const defaultActiveTab = "players";
  const { activeTab } = useParams<TabRouteParams>();
  const navigate = useNavigate();

  // Loads players in parallel; reads from Redux (uses cache if already loaded)
  const { storedPlayerList, fetchingPlayers, fetchError } = usePlayerLoading();

  useEffect(() => {
    if (!activeTab) {
      navigate(`/${defaultActiveTab}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleTab = (tab: string) => {
    if (activeTab !== tab) {
      if (tab === "players") {
        navigate(`/${tab}`);
      } else {
        navigate(`/players/${tab}`);
      }
    }
  };

  useEffect(() => {
    setDisplayBack && setDisplayBack(false);
  }, [setDisplayBack]);

  return (
    <main className="relative pb-20 px-4 z-0 overflow-y-auto w-full">
      <div className="mt-8">
        <div className="max-w-8xl mx-auto w-full flex justify-between items-center pb-2">
          <div className="w-full flex flex-col justify-start px-4 sm:px-6 lg:px-8 lg:pb-6">
            <h2
              className="max-w-7xl mt-4 mb-8 text-3xl leading-6 font-bold text-gray-900"
              data-testid="players-header"
            >
              Players
            </h2>

            <div
              className="w-full border-b border-gray-200"
              data-testid="players-tabs-container"
            >
              <ul
                className="-mb-px flex space-x-8"
                data-testid="players-page-tabs"
              >
                <TabItem
                  page={"players"}
                  title={"All Players"}
                  activeTab={activeTab ? activeTab : defaultActiveTab}
                  toggleTab={toggleTab}
                />
                <TabItem
                  page={"player-groups"}
                  title={"Player Groups"}
                  activeTab={activeTab || "players"}
                  toggleTab={toggleTab}
                />
              </ul>
            </div>
          </div>
        </div>

        <div data-testid="players-page-contents">
          <TabContent
            page={"players"}
            activeTab={activeTab ? activeTab : defaultActiveTab}
          >
            <Roster
              fetchingPlayers={fetchingPlayers}
              storedPlayerList={storedPlayerList}
              fetchError={fetchError}
            />
          </TabContent>
          <TabContent page={"player-groups"} activeTab={activeTab || "players"}>
            <PlayerGroups
              fetchingPlayers={fetchingPlayers}
              storedPlayerList={storedPlayerList}
            />
          </TabContent>
        </div>
      </div>
    </main>
  );
};
